import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { CheckCircle, PersonRemove } from '@mui/icons-material';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';

import CopyableField from '~/components/Common/CopyableField';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import useCurrentBrand from '~/hooks/useCurrentBrand';

import { PromoRole, PromoRoleUser } from '~/services/PromoService';
import UsersService from '~/services/UsersService';

import usePromoAdminPermission from '../hooks/usePromoPermission';

type PromoRoleUserAssignedListProps = {
  role: PromoRole;
  handleUnassignRoleUser: (promoRoleId: string, userId: string) => void;
  roles: Array<PromoRole>;
  addPromoUserToRole?: (promoRoleId: string, userId: string) => void;
  renderExtended: boolean;
  isEditMode: boolean;
  currentUserId: string;
};

const sortUsers = (a: PromoRoleUser, b: PromoRoleUser, currentUserId: string) => {
  if (a.userId === currentUserId) {
    return -1;
  }
  if (b.userId === currentUserId) {
    return 1;
  }
  if (a.updatedAt > b.updatedAt) {
    return -1;
  }
  return 0;
};

function PromoRoleUserAssignedList({
  role,
  handleUnassignRoleUser,
  renderExtended,
  currentUserId,
}: PromoRoleUserAssignedListProps) {
  const isPromoAdmin = usePromoAdminPermission();

  const confirmUnassign = useConfirmDialog();

  const allUserIds = useMemo(() => {
    return role.users.map((user) => user.userId);
  }, [role.users]);

  const brand = useCurrentBrand();
  const [usersMap, setUsersMap] = useState<Map<string, App.UserSummary>>(new Map());
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  useEffect(() => {
    const loadUsers = async () => {
      if (allUserIds.length === 0) return;

      setIsLoadingUsers(true);
      try {
        const users = await UsersService.getUsersSummaryByIds(allUserIds, brand);
        setUsersMap(users);
      } catch (error) {
        console.error('Failed to load users:', error);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    loadUsers();
  }, [allUserIds, brand]);

  const handleUnassign = useCallback(
    async (userId: string, roleId: string) => {
      const confirmed = await confirmUnassign({
        title: 'Unassign User?',
        description: `This will unassign the user from the role, are you sure you want to proceed?`,
      });
      if (!confirmed) {
        return;
      }
      handleUnassignRoleUser(roleId, userId);
    },
    [confirmUnassign, handleUnassignRoleUser, role.roleId],
  );

  if (!role.users?.length) {
    return (
      <Stack direction="column" spacing={0}>
        <Typography variant="caption" color="text.secondary">
          No users assigned
        </Typography>
      </Stack>
    );
  }

  if (!renderExtended) {
    return (
      <Typography variant="caption" title={`Total Users: ${role.users.length}`}>
        {usersMap.get(currentUserId) != null ? (
          <Tooltip title="you have this promo role">
            <CheckCircle color="success" />
          </Tooltip>
        ) : null}
        {isLoadingUsers
          ? 'Loading users...'
          : `${
              role.users.length < 10
                ? `${role.users
                    .slice(0, 6)
                    .map((user) => usersMap.get(user.userId)?.full_name)
                    .join(', ')}`
                : `${role.users.length} users`
            }`}
      </Typography>
    );
  } else {
    return (
      <Box sx={{ maxHeight: '200px', overflow: 'auto' }}>
        <Stack direction="column" spacing={0}>
          {role.users
            .sort((a, b) => sortUsers(a, b, currentUserId))
            .map((user) => (
              <Box key={`user_${user.userId}`} sx={{ border: user.userId == currentUserId ? '3px solid green' : '' }}>
                <CopyableField
                  label={usersMap.get(user.userId)?.full_name ?? user.userId}
                  value={user.userId}
                  variant="caption"
                />
                {isPromoAdmin && (
                  <Button
                    title={`Unassign this user from this role (assigned ${new Date(
                      user.updatedAt,
                    ).toLocaleDateString()})`}
                    onClick={() => handleUnassign(user.userId, role.roleId)}
                    disabled={!isPromoAdmin}
                  >
                    <PersonRemove />
                  </Button>
                )}
              </Box>
            ))}
        </Stack>
      </Box>
    );
  }
}

export default PromoRoleUserAssignedList;
